// TopContractorsTable.jsx

import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FaFileExcel } from "react-icons/fa";
import { Tooltip as InfoTooltip } from "react-tooltip";
import { Info } from "lucide-react";


const Wrapper = styled.div`
  width: 100%;
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  flex-grow: 1;
  max-width: 300px;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #1d6f42;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #155e36;
  }

  svg {
    margin-right: 6px;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #ccc;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 12px;
  background-color: #fff;
  font-size: 0.7rem;

  th, td {
    border: 1px solid #ddd;
    text-align: center;
      padding: 14px 8px;
  line-height: 2;

  }

  th {
    background: #87ceeb;
  }


  tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  tr:hover {
    background-color: #eef6ff;
  }

  .positive {
    background-color: #e7f9ed;
    color: #2e7d32;
    font-weight: 500;
  }

  .negative {
    background-color: #ffeaea;
    color: #c62828;
    font-weight: 500;
  }

  .bold {
    font-weight: bold;
  }

  .rank-up {
    color: green;
    font-weight: bold;
  }

  .rank-down {
    color: red;
    font-weight: bold;
  }

  .rank-neutral {
    color: #666;
  }

  .highlight-top {
    background-color: rgba(0, 128, 0, 0.1);
    font-weight: bold;
  }

  .highlight-low {
    background-color: rgba(255, 0, 0, 0.1);
    font-weight: bold;
  }

  .highlight-neutral {
    background-color: #f0f0f0;
  }
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;


const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;



const formatPLN = (value) =>
  new Intl.NumberFormat("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

  

const TopContractorsTable = ({ data }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("full");

  const { availableYears, availableMonths } = useMemo(() => {
    const years = new Set();
    const months = new Set();
    data.forEach((row) => {
      years.add(parseInt(row.Rok));
      months.add(parseInt(row.Miesiac));
    });
    return {
      availableYears: Array.from(years).sort((a, b) => b - a),
      availableMonths: Array.from(months).sort((a, b) => a - b),
    };
  }, [data]);

  const getMonthName = (monthNumber) =>
    ["Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec", "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"][monthNumber - 1];

  useEffect(() => {
    if (availableYears.length > 0 && selectedYear === null) {
      const latestYear = availableYears[0];
      const monthsInLatestYear = data
        .filter((row) => parseInt(row.Rok) === latestYear)
        .map((row) => parseInt(row.Miesiac));
      const latestMonth = Math.max(...monthsInLatestYear);

      setSelectedYear(latestYear);
      setSelectedMonth(latestMonth.toString());
    }
  }, [availableYears, data, selectedYear]);

  const processedRows = useMemo(() => {
    if (!data || !selectedYear) return [];

    const isFullYear = selectedMonth === "full";
    const latestMonth = isFullYear ? null : parseInt(selectedMonth);
    const prevMonth = latestMonth === 1 ? 12 : latestMonth - 1;
    const prevMonthYear = latestMonth === 1 ? selectedYear - 1 : selectedYear;
    const sameMonthLastYear = selectedYear - 1;

    const getSales = (d) =>
      parseFloat(d.K_15 || d.K_19 || d.K_10 || d.K_21 || d.K_17 || d.K_11 || d.K_22) || 0;

    const groupByKey = (year, month = null) => {
      const map = {};
      data.forEach((d) => {
        if (parseInt(d.Rok) === year && (month === null || parseInt(d.Miesiac) === month)) {
          const key = d.NazwaKontrahenta;
          map[key] = (map[key] || 0) + getSales(d);
        }
      });
      return map;
    };

    const currentMap = isFullYear ? groupByKey(selectedYear) : groupByKey(selectedYear, latestMonth);
    const previousMap = isFullYear ? {} : groupByKey(prevMonthYear, prevMonth);
    const yearAgoMap = isFullYear ? {} : groupByKey(sameMonthLastYear, latestMonth);
    const ytdMap = groupByKey(selectedYear);
    const ytdPrevYearMap = groupByKey(selectedYear - 1);

    let base = Object.entries(currentMap).map(([name, current]) => {
      const prev = previousMap[name] || 0;
      const changeMM = prev === 0 ? null : ((current - prev) / prev) * 100;
      const yoy = yearAgoMap[name] || 0;
      const changeYY = yoy === 0 ? null : ((current - yoy) / yoy) * 100;

      return {
        Nazwa: name,
        CurrentMonth: current,
        PreviousMonth: prev,
        ChangeMM: changeMM,
        YearAgoSameMonth: yoy,
        ChangeYY: changeYY,
        YTD: ytdMap[name] || 0,
        PrevYTD: ytdPrevYearMap[name] || 0,
      };
    });

    base.sort((a, b) => b.YTD - a.YTD);
    base = base.map((row, index) => ({ ...row, Rank: index + 1 }));

    const prevRankMap = Object.entries(ytdPrevYearMap)
      .sort((a, b) => b[1] - a[1])
      .reduce((acc, [name], i) => {
        acc[name] = i + 1;
        return acc;
      }, {});

    return base
      .map((row) => ({
        ...row,
        PrevRank: prevRankMap[row.Nazwa] || null,
        RankChange:
          prevRankMap[row.Nazwa] !== undefined
            ? prevRankMap[row.Nazwa] - row.Rank
            : null,
      }))
      .filter((r) =>
        r.Nazwa.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .slice(0, 10);
  }, [data, searchTerm, selectedYear, selectedMonth]);

  const exportToExcel = () => {
    const rows = processedRows.map((row) => ({
      "Ranking": row.Rank,
      "Nazwa": row.Nazwa,
      "Wartość bieżący m-c": row.CurrentMonth.toFixed(2),
      "Wartość poprzedni m-c": row.PreviousMonth.toFixed(2),
      "Miesiąc / m-c poprzedni (%)": row.ChangeMM === null ? "—" : `${row.ChangeMM.toFixed(2)}%`,
      "Wartość r-1": row.YearAgoSameMonth.toFixed(2),
      "Rok / Rok poprzedni (%)": row.ChangeYY === null ? "—" : `${row.ChangeYY.toFixed(2)}%`,
      "Ranking r-1": row.PrevRank || "-",
      "Zmiana rankingu": row.RankChange !== null ? (row.RankChange > 0 ? `↑ ${row.RankChange}` : row.RankChange < 0 ? `↓ ${Math.abs(row.RankChange)}` : "–") : "-",
      "YTD": row.YTD.toFixed(2),
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "TopKontrahenci");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "top_kontrahenci.xlsx");
  };

  return (
    <Wrapper>
<ChartTitle>
  Top 10 kontrahentów wg wartości sprzedaży
  <InfoIconWrapper data-tooltip-id="top-contractors-tooltip">
    <Info />
  </InfoIconWrapper>
</ChartTitle>

<InfoTooltip
  id="top-contractors-tooltip"
  place="left"
  content={
    <>
      <strong>Ranking kontrahentów</strong>
      <br />
      Tabela pokazuje 10 najlepszych kontrahentów wg sprzedaży YTD.
      <br />
      Uwzględnia zmiany m/m, r/r oraz pozycję w rankingu.
    </>
  }
/>

      <Controls>
        <Select
          value={selectedYear || ""}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
        >
          {availableYears.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </Select>

        <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
          <option value="full">Pełny rok</option>
          {availableMonths.map((month) => (
            <option key={month} value={month}>
              {getMonthName(month)}
            </option>
          ))}
        </Select>

        <SearchInput
          type="text"
          placeholder="Szukaj po nazwie..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <ExportButton onClick={exportToExcel}>
          <FaFileExcel />
          Eksportuj do Excel
        </ExportButton>
      </Controls>

      <div style={{ overflowX: "auto", width: "100%" }}>
        <StyledTable>
<thead>
  <tr>
    <th>#</th> {/* position */}
    <th>Nazwa</th>
    <th>Wartość bieżący m-c</th>
    <th>Wartość poprzedni m-c</th>
    <th>Wartość r-1</th>
    <th>m/m (%)</th>
    <th>r/r (%)</th>
    <th>Ranking</th>
    <th>Ranking r-1</th>
    <th>Zmiana rankingu</th>
    <th>YTD</th>
  </tr>
</thead>

<tbody>
  {processedRows.map((row, i) => (
    <tr key={i}>
      <td className="bold">{row.Rank}</td>
      <td className="bold" title={row.Nazwa}>
        {row.Nazwa.length > 15 ? `${row.Nazwa.slice(0, 15)}...` : row.Nazwa}
      </td>
      <td>{formatPLN(row.CurrentMonth)}</td>
      <td>{formatPLN(row.PreviousMonth)}</td>
            <td>{formatPLN(row.YearAgoSameMonth)}</td>
<td
  className={`bold ${
    row.ChangeMM === null
      ? ""
      : row.ChangeMM >= 0
      ? "positive"
      : "negative"
  }`}
>
  {row.ChangeMM === null ? "—" : `${row.ChangeMM.toFixed(2)}%`}
</td>

<td
  className={`bold ${
    row.ChangeYY === null
      ? ""
      : row.ChangeYY >= 0
      ? "positive"
      : "negative"
  }`}
>
  {row.ChangeYY === null ? "—" : `${row.ChangeYY.toFixed(2)}%`}
</td>

      <td
        className={
          row.Rank <= 3
            ? "highlight-top"
            : row.Rank >= 8
            ? "highlight-low"
            : "highlight-neutral"
        }
      >
        {row.Rank}
      </td>
      <td
        className={
          row.PrevRank <= 3
            ? "highlight-top"
            : row.PrevRank >= 8
            ? "highlight-low"
            : "highlight-neutral"
        }
      >
        {row.PrevRank || "-"}
      </td>
      <td
        className={
          row.RankChange > 0
            ? "rank-up"
            : row.RankChange < 0
            ? "rank-down"
            : "rank-neutral"
        }
      >
        {row.RankChange !== null
          ? row.RankChange > 0
            ? `↑ ${row.RankChange}`
            : row.RankChange < 0
            ? `↓ ${Math.abs(row.RankChange)}`
            : "–"
          : "-"}
      </td>
      <td>{formatPLN(row.YTD)}</td>
    </tr>
  ))}
</tbody>

        </StyledTable>
      </div>
    </Wrapper>
  );
};

export default TopContractorsTable;